<template>
  <div class="relative col-span-2 space-y-4">
    <BookingRangePicker
      v-if="!hideBookingRangePicker"
      :start-date="formValues.startDate!"
      :end-date="formValues.endDate!"
      class="col-span-2"
      :booking-id="props.booking.id"
      :disabled="disabled"
      :start-date-title="t('rentalStart')"
      :end-date-title="t('rentalEnd')"
      @change="handleTimeRangeChange"
    />

    <div v-else class="col-span-2 grid w-full grid-cols-2 space-x-2">
      <LabeledText class="px-1" :label="t('rentalStart')">
        {{ formatDate(formValues.startDate!) }}
      </LabeledText>
      <LabeledText class="px-1" :label="t('rentalEnd')">
        {{ formatDate(formValues.endDate!) }}</LabeledText
      >
    </div>

    <DisplayLocation v-if="showLocations" :booking="booking" hide-station-addresses />

    <div v-if="hasChanged" class="mt-2">
      <p v-if="durationTooShort" class="mb-2 text-sm text-red-700">{{ t('minDuration') }}</p>
      <div class="flex justify-end gap-2">
        <CVButton
          variant="default"
          :disabled="durationTooShort"
          :is-loading="isSaving"
          @click.prevent="$emit('save')"
        >
          {{ t('save') }}
        </CVButton>
        <CVButton :disabled="isSaving" @click.prevent="$emit('cancel')">
          {{ t('cancel') }}
        </CVButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Booking, UpdateBookingDto } from '@/entities/bookings/booking.entity';
import { useI18n } from 'vue-i18n';
import BookingRangePicker from '@/views/handover/components/overview/BookingRangePicker.vue';
import { ref, computed, watch } from 'vue';
import LabeledText from '../LabeledText.vue';
import DisplayLocation from '../booking-forms/DisplayLocation.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { formatDate } from '@/hooks/use-formatted-date.ts';
import { useFormContext } from 'vee-validate';
import { isLongTermPricing } from '@/utils/pricing.utils';
import { isLongTermDurationTooShort } from '@/utils/long-term-create.utils';

const props = defineProps<{
  booking: Booking;
  showLocations?: boolean;
  hideBookingRangePicker?: boolean;
  disabled?: boolean;
  isSaving?: boolean;
}>();

const emit = defineEmits<{
  (e: 'hasChanged', value: boolean): void;
  (e: 'save'): void;
  (e: 'cancel'): void;
}>();

const { t } = useI18n();

const hasChanged = ref(false);

const { values: formValues, setFieldValue, isFieldDirty } = useFormContext<UpdateBookingDto>();

const durationTooShort = computed(
  () =>
    isLongTermPricing(props.booking.priceCalculation) &&
    !!formValues.startDate &&
    !!formValues.endDate &&
    isLongTermDurationTooShort(new Date(formValues.startDate), new Date(formValues.endDate)),
);

const handleTimeRangeChange = async (startDate: string, endDate: string) => {
  setFieldValue('startDate', startDate);
  setFieldValue('endDate', endDate);
};

watch([() => formValues.startDate, () => formValues.endDate], () => {
  hasChanged.value = isFieldDirty('startDate') || isFieldDirty('endDate');
  emit('hasChanged', hasChanged.value);
});
</script>

<i18n lang="json">
{
  "en": {
    "rentalStart": "Rental Start",
    "rentalEnd": "Rental End",
    "minDuration": "Long-term bookings must be at least 30 days.",
    "vehicleNotAvailableSelectAlternative": "The vehicle is not available in this time period. Please choose an alternative:",
    "noAlternativeVehicleAvailable": "No alternative vehicle available. Please try another time period."
  },
  "de": {
    "rentalStart": "Mietbeginn",
    "rentalEnd": "Mietende",
    "minDuration": "Langzeitmieten müssen mindestens 30 Tage dauern.",
    "vehicleNotAvailableSelectAlternative": "Das Fahrzeug ist in dem Zeitraum nicht verfügbar. Bitte wählen Sie eine Alternative:",
    "noAlternativeVehicleAvailable": "Kein Alternativfahrzeug verfügbar. Bitte versuchen Sie einen anderen Zeitraum."
  },
  "es": {
    "rentalStart": "Inicio del alquiler",
    "rentalEnd": "Fin del alquiler",
    "minDuration": "Los alquileres a largo plazo deben durar al menos 30 días.",
    "vehicleNotAvailableSelectAlternative": "El vehículo no está disponible en este periodo. Elija una alternativa:",
    "noAlternativeVehicleAvailable": "No hay vehículo alternativo disponible. Pruebe con otro periodo."
  }
}
</i18n>
